import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import integrationGroups from "../data/integrations";

// Closing scene of the hero walkthrough: Aivio's coordinator wired to the business systems its agents
// can work in, with data moving along each connection.
const AREAS = ["SALES & CRM", "PROCUREMENT & ERP", "CUSTOMER SERVICE", "COLLABORATION", "PEOPLE OPERATIONS", "IT & SERVICE MANAGEMENT"]
  .map((label) => integrationGroups.find((group) => group.label === label))
  .filter(Boolean);
const APP_COUNT = new Set(integrationGroups.flatMap((group) => group.apps)).size;
const titleCase = (label) => label.toLowerCase().replace(/\b(\w)/g, (c) => c.toUpperCase()).replace(/\bIt\b/, "IT").replace(/\bErp\b/, "ERP").replace(/\bCrm\b/, "CRM");
const initials = (app) => app.split(/\s+/).filter((word) => /^[A-Za-z]/.test(word)).slice(0, 2).map((word) => word[0]).join("").toUpperCase();

function WalkthroughConnect({ fading, usedArea }) {
  const mapRef = useRef(null);
  const hubRef = useRef(null);
  const cardRefs = useRef([]);
  const [paths, setPaths] = useState([]);

  // Draw a curve from the hub to each card, in the map's own pixel space.
  useLayoutEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const draw = () => {
      const box = map.getBoundingClientRect();
      const hub = hubRef.current.getBoundingClientRect();
      const cx = hub.left - box.left + hub.width / 2;
      const cy = hub.top - box.top + hub.height / 2;
      setPaths(cardRefs.current.filter(Boolean).map((card) => {
        const r = card.getBoundingClientRect();
        const left = r.left - box.left < cx;
        const x = left ? r.right - box.left : r.left - box.left;
        const y = r.top - box.top + r.height / 2;
        const sx = left ? cx - hub.width / 2 : cx + hub.width / 2;
        const bend = (x - sx) * 0.5;
        return `M ${sx} ${cy} C ${sx + bend} ${cy}, ${x - bend} ${y}, ${x} ${y}`;
      }));
    };
    draw();
    const observer = "ResizeObserver" in window ? new ResizeObserver(draw) : null;
    observer?.observe(map);
    return () => observer?.disconnect();
  }, []);

  const card = (group, index) => (
    <div className={`hw-sys${group.label === usedArea ? " is-used" : ""}`} key={group.label} ref={(node) => { cardRefs.current[index] = node; }} style={{ "--i": index }}>
      <b>{titleCase(group.label)}{group.label === usedArea && <em>Used just now</em>}</b>
      <div>{group.apps.slice(0, 2).map((app) => <span key={app}><i>{initials(app)}</i>{app}</span>)}</div>
    </div>
  );

  return (
    <div className={`hw-connect${fading ? " is-fading" : ""}`}>
      <div className="hw-connect-head">
        <span className="hw-connect-kicker">Integrations</span>
        <h3>Your agents, connected to the systems <span>your teams already use.</span></h3>
        <p>{APP_COUNT} example apps across {integrationGroups.length} business areas · connections depend on your environment</p>
      </div>
      <div className="hw-connect-map" ref={mapRef}>
        <svg className="hw-connect-lines" aria-hidden="true">
          {paths.map((d, index) => (
            <g key={index}>
              <path d={d} />
              <circle r="3.5">
                <animateMotion dur={`${2.2 + (index % 3) * 0.35}s`} begin={`-${index * 0.45}s`} repeatCount="indefinite" path={d}
                  keyPoints={index % 2 ? "1;0" : "0;1"} keyTimes="0;1" calcMode="linear" />
              </circle>
            </g>
          ))}
        </svg>
        <div className="hw-connect-col">{AREAS.slice(0, 3).map((group, index) => card(group, index))}</div>
        <div className="hw-hub" ref={hubRef}>
          <span className="hw-hub-ring" aria-hidden="true" /><span className="hw-hub-ring is-outer" aria-hidden="true" />
          <span className="hw-hub-mark">✦</span><b>Aivio</b><small>Coordinator</small>
        </div>
        <div className="hw-connect-col">{AREAS.slice(3).map((group, index) => card(group, index + 3))}</div>
      </div>
      <div className="hw-connect-actions">
        <Link to="/use-cases#integrations" className="hw-connect-primary">See all integrations <span aria-hidden="true">→</span></Link>
        <a href="#agents" className="hw-connect-secondary">Watch agents use them <span aria-hidden="true">↓</span></a>
      </div>
    </div>
  );
}

export default WalkthroughConnect;
